export type CookieConsentEvent =
    | "CookieConsent.change"
    | "CookieConsent.reject"
    | "CookieConsent.initialize";

export interface CookieSettings {
    name: string;
    expires: number;
}
export interface CookieConsentOptions {
    options: CookieSettings;
    removeAboutUs: string;
    cookies: any[];
}

// Fonction pour obtenir la date d'expiration d'un cookie
function getCookieExpireDate(cookieTimeout: number): string {
    cookieTimeout = cookieTimeout || 33696000000; // 13 mois par défaut
    const date = new Date();
    date.setTime(date.getTime() + cookieTimeout);
    return "; expires=" + date.toUTCString();
}

function deleteCookiesByPattern(pattern: string): void {
    const regex = new RegExp(`^${pattern.replace("*", ".*")}$`);
    const cookies = document.cookie.split("; ");
    cookies.forEach(cookie => {
        const cookieName = cookie.split("=")[0];
        if (regex.test(cookieName)) {
            delCookie(cookieName.trim());
        }
    });
}

// Fonction pour supprimer un cookie
function delCookie(name: string): void {
    const path = ";path=/";
    const hostname = document.location.hostname.replace(/^www\./, "");
    const domain = ";domain=." + hostname;
    const expiration = "Thu, 01 Jan 1970 00:00:01 GMT";
    document.cookie = `${name}=;${path}${domain};expires=${expiration}`;
}

// Fonction pour récupérer un cookie par son nom
function getCookie(name: string): string | null {
    const cookies = document.cookie.split("; ");
    for (const cookie of cookies) {
        const [key, value] = cookie.split("=");
        if (key === name) {
            return decodeURIComponent(value);
        }
    }
    return null;
}

interface ICookieItem {
    key: string; // Identifiant unique du cookie
    description: string; // Description du cookie
    cookies: string[]; // Liste des cookies associés
}

// Classe principale pour gérer le consentement aux cookies
class CookieConsent {
    private cookieName: string;
    private expiration: number;
    private modalConsent: string;
    public cookieItems: ICookieItem[] = [];
    public whiteList: string[] = [];
    public blackList: string[] = [];
    private onStatusChange() {
        const instance = this;
        const cookiesConsents = this.cookiesConsents;
        document.dispatchEvent(new CustomEvent('CookieConsent.change', {
            detail: { instance, cookiesConsents }
        }));
    };
    private onInitialize (instance: CookieConsent, event: any) {
        document.dispatchEvent(new CustomEvent('CookieConsent.initialize', {
                detail: { instance }
        }));
    };
    private onReject(cookieName: string) {
        const instance = this;
        document.dispatchEvent(new CustomEvent('CookieConsent.reject', {
            detail: {  instance ,  cookieName}
        }));
    };

    constructor(cookieConsentOptions: CookieConsentOptions, modalConsent: string) {
        this.cookieName = cookieConsentOptions.options.name;
        this.expiration = cookieConsentOptions.options.expires;
        this.modalConsent = modalConsent;

        this.cookieItems = cookieConsentOptions?.cookies || [];

        if (!Array.isArray(this.cookieItems) || this.cookieItems.length === 0) {
            console.warn("cookieItems est vide ou mal structuré.");
        }

        this.initialize();
    }

    private get cookiesConsents(): string[] {
        const cookieContents: string[] = [];

        for (const item of this.cookieItems) {
            // Met à jour uniquement les cases à cocher associées en utilisant les API natives
            const checkbox = document.querySelector<HTMLInputElement>(`[data-cookie-consent='${item.key}'] > input`);
            if (checkbox && checkbox.checked) {
                cookieContents.push(item.key);
            }
        }

        return cookieContents;
    }

    private set cookiesConsents(values: string[]){

        for (const item of this.cookieItems) {
            // Met à jour uniquement les cases à cocher associées en utilisant les API natives
            const checkbox = document.querySelector<HTMLInputElement>(`[data-cookie-consent='${item.key}'] > input`);
            if (checkbox) {
                checkbox.checked = values.indexOf(item.key) !== -1;
            }
        }
    }
    public acceptAll(): void {
        document.cookie = `${this.cookieName}=${this.cookieItems.map(c => c.key).join(",")}; ${getCookieExpireDate(this.expiration)}; path=/; Secure; SameSite=None`;

        if (this.onStatusChange) {
            this.onStatusChange();
        }

        this.hideBanner();
        this.hideInform();
    }

    public acceptAllAndSaveSettings(): void {
        document
            .querySelectorAll('[data-cookie-consent] input[type="checkbox"]')
            .forEach(cb => (cb as HTMLInputElement).checked = true);

        setTimeout(() => {
            this.acceptAll();
        }, 1000);
    }

    public rejectAll(): void {
        // Enregistrer une valeur vide dans le cookie
        this.setCookie(this.cookieName, "", this.expiration);

        this.delRejected();
        this.hideBanner();
        if (this.onStatusChange) {
            this.onStatusChange();
        }
    }

    public get cookieConsentHasConsented(): boolean {
        const cookieValue = getCookie(this.cookieName);
        return cookieValue !== null;
    }

    public getConsentedCookies(): string[] {
        const cookieValue = getCookie(this.cookieName);
        return cookieValue ? cookieValue.split(",") : [];
    }

    public prefillCookieSettings(): void {
        const consentedCookies = this.getConsentedCookies();

        this.cookieItems.forEach(item => {
            const checkbox = document.querySelector<HTMLInputElement>(`#chkGroupOnOff_${item.key}`);
            if (checkbox) {
                checkbox.checked = consentedCookies.indexOf(item.key) !== -1;
            }
        });
    }

    private initialize(): void {
        const self = this;
        if (this.cookieConsentHasConsented) {
            /*this.showBackdrop();*/
            this.hideBanner();
        } else {
            //this.hideBackdrop();
            this.showBanner();
            this.delRejected();
        }
        const myModalEl = document.querySelector<HTMLDivElement>('#modalConsentCookies');
        myModalEl?.addEventListener('show.bs.modal', event => {
            self.prefillCookieSettings();
        });
        // Interception des cookies
        this.interceptCookies();
        if (this.cookieConsentHasConsented) {
            this.onStatusChange();
        }
    }

    private setCookie(name: string, value: string, expiration: number): void {
        const date = new Date();
        date.setTime(date.getTime() + expiration);
        document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; Secure; SameSite=None`;
    }

    private showBanner(): void {
        const banner = document.getElementById("cookie-banner");
        if (banner) {
            banner.style.visibility = "visible";
            this.showBackdrop();
        }
    }

    public hideBanner(): void {
        const banner = document.getElementById("cookie-banner");
        if (banner) {
            banner.style.visibility = "hidden";
        }
        this.hideBackdrop();
    }

    private showBackdrop(): void {
        let backdrop = document.querySelector(".cookie-backdrop") as HTMLElement;
        if (!backdrop) {
            backdrop = document.createElement("div") as HTMLElement;
            backdrop.className = "modal-backdrop fade show cookie-backdrop";
            document.body.appendChild(backdrop);
        }
        backdrop.style.display = "block";
    }

    private hideBackdrop(): void {
        const backdrop = document.querySelector(".cookie-backdrop") as HTMLElement;
        if (backdrop) {
            backdrop.style.display = "none";
        }
    }

    public showInform(): void {
        this.prefillCookieSettings();

        const modalElement = document.getElementById("modalConsentCookies");
        const backdrop = document.getElementById("modalConsentCookiesBackdrop");

        if (modalElement) {
            modalElement.classList.add("show");
            modalElement.style.display = "block";
            modalElement.removeAttribute("aria-hidden");
        } else {
            console.error("La modale 'modalConsentCookies' n'a pas été trouvée dans le DOM.");
        }

        if (backdrop) {
            backdrop.style.display = "block";
            backdrop.classList.add("show");
        }
    }

    public hideInform(): void {
        const modalElement = document.getElementById("modalConsentCookies");
        //const backdrop = document.querySelector(".modal-backdrop");
        const backdrop = document.getElementById("modalConsentCookiesBackdrop");

        if (modalElement) {
            modalElement.classList.remove("show");
            modalElement.style.display = "none";
            modalElement.setAttribute("aria-hidden", "true");
        }

        if (backdrop) {
            backdrop.style.display = "none";
            backdrop.classList.remove("show");
        }
    }

    public saveSettings(): void {
        const consentedCookies: string[] = [];

        // Récupérer uniquement les clés des groupes cochés
        this.cookieItems.forEach(item => {
            const checkbox = document.querySelector<HTMLInputElement>(`#chkGroupOnOff_${item.key}`);
            if (checkbox && checkbox.checked) {
                consentedCookies.push(item.key);
            }
        });

        document.cookie = `${this.cookieName}=${consentedCookies.join(",")}; ${getCookieExpireDate(this.expiration)}; path=/; Secure; SameSite=None`;

        this.delRejected();

        if (this.onStatusChange) {
            this.onStatusChange();
        }

        this.hideInform();
        this.hideBanner();
    }

    private delRejected(): void {
        this.cookieItems.forEach(item => {
            if (!this.isGranted(item.key)) {
                item.cookies.forEach(cookie => {
                    if (cookie.includes("*")) {
                        // Supprime les cookies correspondant au motif
                        deleteCookiesByPattern(cookie);
                    } else {
                        // Supprime un cookie spécifique
                        delCookie(cookie.trim());
                    }
                });
            }
        });
    }

    public isGranted(key: string): boolean {
        var cookie = getCookie(this.cookieName);
        if (cookie !== null) {
            return cookie.split(',').indexOf(key) > -1;
        } else {
            return false;
        }
    }

    private interceptCookies(): void {
        const cookieDesc = Object.getOwnPropertyDescriptor(Document.prototype, "cookie") ||
            Object.getOwnPropertyDescriptor(HTMLDocument.prototype, "cookie");

        if (cookieDesc && cookieDesc.configurable) {
            Object.defineProperty(document, "cookie", {
                get: () => cookieDesc.get?.call(document),
                set: (val: string) => {
                    const nameOfCookie = val.split("=")[0];
                    if (this.blackList.indexOf(nameOfCookie) !== -1 || this.isRevoked(nameOfCookie)) {
                        console.warn(`Cookie '${nameOfCookie}' bloqué par CookieConsent.`);
                        if (this.onReject) {
                            this.onReject(nameOfCookie);
                        }
                        return;
                    }
                    cookieDesc.set?.call(document, val);
                }
            });
        }
    }

    public isRevoked(name: string): boolean {
        for (var i = 0; i < this.cookieItems.length; i++) {
            if (name == this.cookieItems[i].key) {
                return !this.isGranted(name);
            }
            for (var j = 0; j < this.cookieItems[i].cookies.length; j++) {
                if (name == this.cookieItems[i].cookies[j]) {
                    return !this.isGranted(this.cookieItems[i].key);
                }
            }
        }

        return false;
    }
}
const cookieConsentConfig: CookieConsentOptions = window["cookieConsentConfig"] || {};

window["CookieConsent"] = CookieConsent;
export const cookieConsent = new CookieConsent(cookieConsentConfig, "#modalConsentCookies");
window["_cookieConsent"] = cookieConsent;

// Initialisation après le chargement du DOM
document.addEventListener("DOMContentLoaded", () => {
    const cookiesToManage = cookieConsentConfig.cookies || [];

    cookieConsent.cookieItems = cookiesToManage;
});